import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { GenotypingBrapiService } from '../../../shared/brapi/service/genotyping-brapi.service';
import { GigwaExportRequest } from '../../../shared/brapi/model/export/gigwa-export-request';
import { GA4GHSearchRequest } from '../../../shared/brapi/model/export/ga4gh-search-request';
import { AlertService } from '../../../shared/alert/alert.service';
import { VariantSet } from '../../../shared/brapi/model/variantsets/variantset';
import { forkJoin, Observable, throwError, timer } from 'rxjs';
import { catchError, mergeMap, retryWhen } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { CropGenotypingParameter } from '../../../shared/crop/model/crop-genotyping-parameter';
import * as flapjack from 'flapjack-bytes/src/flapjack-bytes';
import { BmsjHipsterSharedModule } from '../../../shared';
import { NgSelectModule } from '@ng-select/ng-select';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TreeModule } from 'primeng/tree';
import { Germplasm } from '../../../shared/brapi/model/germplasm/germplasm';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'jhi-flapjack-view',
    standalone: true,
    imports: [
        BmsjHipsterSharedModule,
        NgSelectModule,
        ReactiveFormsModule,
        TranslateModule,
        TreeModule
    ],
    templateUrl: './flapjack-view.component.html',
    styleUrl: './flapjack-view.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class FlapjackViewComponent implements OnInit {

  @Input() selectedVariantSet: VariantSet;
  @Input() cropGenotypingParameter: CropGenotypingParameter;
  @Input() genotypingGermplasm: Germplasm;

  isLoadingFlapjack = false;

  constructor(private genotypingBrapiService: GenotypingBrapiService,
              private alertService: AlertService,
              private activeModal: NgbActiveModal,
              private http: HttpClient) {
  }

  ngOnInit(): void {
      this.loadFlapjackBytes();
  }

  closeModal() {
      this.activeModal.close();
  }

  loadFlapjackBytes() {
      if (this.selectedVariantSet) {
          this.isLoadingFlapjack = true;

          const gigwaExportRequest = new GigwaExportRequest();
          gigwaExportRequest.variantSetId = this.selectedVariantSet.referenceSetDbId;
          gigwaExportRequest.exportFormat = 'FLAPJACK';
          gigwaExportRequest.exportedIndividuals = [this.genotypingGermplasm.germplasmName];
          gigwaExportRequest.keepExportOnServer = true;

          const ga4GHVariantSearchRequest = new GA4GHSearchRequest();
          ga4GHVariantSearchRequest.variantSetId = this.selectedVariantSet.referenceSetDbId;

          // Before exporting the data into Flapjack format, invoking Variants Search first is required in order to be able to export
          // because this is what tells the Gigwa which variants to include.
          this.genotypingBrapiService.ga4GhVariantsSearch(ga4GHVariantSearchRequest).toPromise().then((searchResult) => {
              if (searchResult.count) {
                  // Export the gigwa data to in FLAPJACK format
                  this.genotypingBrapiService.gigwaExportData(gigwaExportRequest).toPromise().then((response) => {

                      let file = response.replace('.fjzip', '');
                      file = this.extractHostName(this.cropGenotypingParameter.baseUrl) + file;

                      // Check first if the map and genotype files are available in the server
                      // before rendering the flapjack bytes.
                      this.checkUrls([`${file}.map`, `${file}.genotype`], 50, 3000).subscribe((data) => {
                          this.isLoadingFlapjack = false;
                          const flapjackDiv = '#flapjack-div';
                          const renderer = flapjack.default();
                          const width = document.querySelector(flapjackDiv).getBoundingClientRect().width;
                          renderer.renderGenotypesUrl({
                              domParent: flapjackDiv,
                              width: document.querySelector(flapjackDiv).getBoundingClientRect().width,
                              height: 250,
                              mapFileURL: `${file}.map`,
                              genotypeFileURL: `${file}.genotype`,
                              overviewWidth: document.querySelector(flapjackDiv).getBoundingClientRect().width,
                              overviewHeight: 25,
                              minGenotypeAutoWidth: document.querySelector(flapjackDiv).getBoundingClientRect().width,
                              minOverviewAutoWidth: document.querySelector(flapjackDiv).getBoundingClientRect().width,
                              dataSetId: this.cropGenotypingParameter.programId
                          });
                      },
                      (error) => {
                          this.alertService.error('genotyping.system.cannot.retrieve.data.files');
                          console.error('Error: ', error);
                      }
                      );

                  });
              } else {
                  this.alertService.error('genotyping.no.genotyping.callsets.found');
              }
          }).finally(() => {
              this.isLoadingFlapjack = false;
          });
      }
  }

  private extractHostName(baseUrl) {
      const { hostname, protocol } = new URL(baseUrl);
      return protocol + '//' + hostname;
  }

  /**
   *  This method recursively check the URLs until all of them return a 200 status code or until the maximum number of retries is reached.
   *  Adjust the parameters (such as maxRetries and retryDelay) based on your requirements.
   * @param urls
   * @param maxRetries
   * @param retryDelay
   */
  private checkUrls(urls: string[], maxRetries = 5, retryInterval = 1000): Observable<any[]> {
      const observables = urls.map((url) => this.checkUrl(url, maxRetries, retryInterval));
      return forkJoin(observables);
  }

  private checkUrl(url: string, maxRetries: number, retryInterval: number): Observable<any> {
      let retries = 0;

      return this.http.get(url, { responseType: 'text' }).pipe(
          retryWhen((errors) => errors.pipe(
              mergeMap((error, index) => {
                  if (++retries <= maxRetries) {
                      return timer(retryInterval);
                  }
                  return throwError(error);
              })
          )),
          catchError((error) => {
              return throwError(error);
          })
      );
  }

}
